import { Box, Text } from '@shopify/polaris';
import { useCurrency } from '../../../../customHooks/useCurrency';
import { TableShell } from './TableShell';
import {
  TABLE_BODY_TEXT,
  TABLE_CELL_BORDER,
  TABLE_CELL_PADDING,
  TABLE_DANGER,
  TABLE_HEADER_BG,
  TABLE_HEADER_TEXT_STRONG,
  TABLE_POSITIVE,
} from './tableStyles';
import { formatMoney, formatUnits } from './formatters';

export const DiscountScale = ({ m, neutralDiscount }) => {
  const { currency } = useCurrency();
  const fmt$ = (n) => formatMoney(n, currency);

  const remainUnits = m.totalRemainUnits;
  const buyValue = m.totalBuyValue;
  const pvfTotal = m.totalSellValuePvfTotal;
  // PVP unitario base = promedio observado en las ventas ya realizadas.
  const basePvpUnit = m.totalSellUnitsTotal > 0 ? m.totalSellValuePvpTotal / m.totalSellUnitsTotal : 0;
  const basePvfUnit = basePvpUnit / 1.16; // Asumo IVA 16% MX

  if (remainUnits <= 0 || basePvpUnit <= 0)
    return <Text tone="subdued">No hay stock restante o no hay precio base de referencia para simular.</Text>;

  // Punto neutro analítico: descuento d* tal que pvf + remain × pvp × (1 − d*) = compra.
  const merchantValue = remainUnits * basePvfUnit;

  const discounts = [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8];
  const scenarios = discounts.map((d) => {
    const extraIncome = merchantValue * (1 - d);
    const balance = pvfTotal + extraIncome - buyValue;
    const unitSellPrice = basePvpUnit * (1 - d);
    // Margen sobre la inversión inicial: qué % del coste se obtiene de
    // beneficio neto en el escenario completo (lo vendido + liquidación
    // del resto al PVP con descuento d).
    const margin = buyValue > 0 ? balance / buyValue : null;
    return { d, unitSellPrice, extraIncome, balance, margin };
  });

  return (
    <>
      <TableShell>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
            <thead>
              <tr>
                {[
                  ['Descuento sobre PVP', 'left'],
                  ['PVP aplicado', 'right'],
                  ['Ingresos del resto', 'right'],
                  ['Rendimiento final', 'right'],
                  ['Margen sobre compra', 'right'],
                ].map(([label, align]) => (
                  <th
                    key={label}
                    style={{
                      ...TABLE_CELL_PADDING,
                      textAlign: align,
                      backgroundColor: TABLE_HEADER_BG,
                      color: TABLE_HEADER_TEXT_STRONG,
                      fontSize: 12,
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      borderBottom: TABLE_CELL_BORDER,
                    }}
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {scenarios.map((s) => {
                const positive = s.balance >= 0;
                const tdNum = {
                  ...TABLE_CELL_PADDING,
                  textAlign: 'right',
                  borderBottom: TABLE_CELL_BORDER,
                  color: TABLE_BODY_TEXT,
                };
                const tdAcc = {
                  ...tdNum,
                  color: positive ? TABLE_POSITIVE : TABLE_DANGER,
                  fontWeight: 700,
                };
                return (
                  <tr key={s.d} className="efftable-row">
                    <td
                      style={{
                        ...TABLE_CELL_PADDING,
                        fontWeight: 600,
                        borderBottom: TABLE_CELL_BORDER,
                        color: TABLE_BODY_TEXT,
                      }}
                    >
                      {`-${(s.d * 100).toFixed(0)}%`}
                    </td>
                    <td style={tdNum}>{fmt$(s.unitSellPrice)}/ud</td>
                    <td style={tdNum}>{fmt$(s.extraIncome)}</td>
                    <td style={tdAcc}>{fmt$(s.balance)}</td>
                    <td style={tdAcc}>{s.margin === null ? '—' : `${(s.margin * 100).toFixed(1)}%`}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </TableShell>
      <Box minHeight=".5rem" />
      <Text variant="bodySm" tone="subdued">
        {`Quedan ${formatUnits(remainUnits)} uds. · PVP unitario base estimado: ${fmt$(basePvpUnit)} (promedio observado en lo ya vendido)`}
      </Text>
    </>
  );
};
